import cx from "clsx";
import ThemeProvider from "kreate-common/modules/kreate-ui/components/ThemeProvider";
import React from "react";

import Layout from "../PageGalleryCollector/components/Layout";
import GalleryArtworkExplorer from "../PageGalleryCollector/containers/GalleryArtworkExplorer";
import { useGalleryArtworkExplorerApi } from "../PageGalleryCollector/containers/GalleryArtworkExplorer/hooks/useGalleryArtworkExplorerApi";
import GallerySidebar from "../PageGalleryCollector/containers/GallerySidebar";
import { useGallerySidebarApi } from "../PageGalleryCollector/containers/GallerySidebar/hooks/useGallerySidebarApi";
import SectionHeadline from "../PageGalleryCollector/containers/SectionHeadline";

import styles from "./index.module.scss";
import { getPageTitle } from "./utils";

import { getThumbnailUrlFromBlobId } from "@/modules/kreate-components/components/ImageViewer/utils";
import KreateHead from "@/modules/kreate-components/components/KreateHead";
import NavbarV4 from "@/modules/kreate-components/components/NavbarV4";
import { useThemeFromSessionPreferences } from "@/modules/kreate-components/hooks/useThemeFromSessionPreferences";
import { getLinkToPageGalleryCollector$ForSale } from "@/modules/navigation/utils/getLinkTo";
import { useGalleryCollector$GivenAddressIsh } from "@/modules/resources/hooks/useGalleryCollector$GivenAddressIsh";

// TODO: apply the new layout

type AddressIsh = string; // address (addr1...) or ADA handle ($...)

type Props = {
  className?: string;
  style?: React.CSSProperties;
  addressIsh: AddressIsh;
};

export default function PageGalleryCollector$ForSale({
  className,
  style,
  addressIsh,
}: Props) {
  const fromSessionPreferences = useThemeFromSessionPreferences();
  const [galleryCollector] = useGalleryCollector$GivenAddressIsh(addressIsh);

  const gallerySidebarApi = useGallerySidebarApi({
    galleryCollector,
    sidebarLocation: { type: "for-sale" },
    addressIsh,
  });

  const galleryArtworkExplorerApi = useGalleryArtworkExplorerApi({
    addressIsh,
    galleryAlbumId: undefined,
    otherFetchParams: { forSale: true },
  });

  const galleryInfo =
    galleryArtworkExplorerApi.galleryCollector?.populated?.galleryInfo;

  return (
    <ThemeProvider theme={fromSessionPreferences.theme} asChild>
      <Layout.Root className={cx(styles.container, className)} style={style}>
        <KreateHead
          title={getPageTitle({ galleryInfo, addressIsh })}
          description={galleryInfo?.description}
          imageUrl={
            galleryInfo?.avatar
              ? getThumbnailUrlFromBlobId(galleryInfo.avatar.blob.blobId)
              : undefined
          }
          url={undefined}
        />
        <Layout.Header>
          <NavbarV4
            product="market"
            hideRibbonCarousel
            makeTransparentUnlessScrolledDown
          />
        </Layout.Header>
        <Layout.BeforeMain>
          <SectionHeadline
            galleryCollector={galleryCollector}
            addressIsh={addressIsh}
          />
        </Layout.BeforeMain>
        <Layout.Main>
          <Layout.Left>
            <GallerySidebar api={gallerySidebarApi} />
          </Layout.Left>
          <Layout.Center>
            <GalleryArtworkExplorer.Layout.Root>
              <GalleryArtworkExplorer.PanelTitle
                api={galleryArtworkExplorerApi}
                title="For Sale"
                buttonShare={{
                  content: "Share",
                  url: getLinkToPageGalleryCollector$ForSale(addressIsh),
                  pageTitle: getPageTitle({ galleryInfo, addressIsh }),
                }}
              />
              <GalleryArtworkExplorer.Layout.Divider />
              <GalleryArtworkExplorer.PanelFiltersAndControls
                api={galleryArtworkExplorerApi}
                canSortByPrice={true}
              />
              <GalleryArtworkExplorer.PanelGridArtworks
                api={galleryArtworkExplorerApi}
                statsDisplayMode="listed-price"
                defaultTabWhenVisitingPageGalleryArtwork="buy"
              />
            </GalleryArtworkExplorer.Layout.Root>
          </Layout.Center>
        </Layout.Main>
      </Layout.Root>
    </ThemeProvider>
  );
}
